import type {
  ProductDetail,
  SyncProductDetailEntry,
} from '~/features/onboarding/types/product-overview.types';

/** Loose product row from `POST products/{connectionId}/list`. */
type CatalogRow = Record<string, unknown>;

/** Coerce a loose value to a display string (objects/nullish → ''). */
function str(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/** First non-empty stringified value across the given keys. */
function pick(row: CatalogRow, ...keys: string[]): string {
  for (const key of keys) {
    const s = str(row[key]);
    if (s) return s;
  }
  return '';
}

/** Non-empty string entries of the first array-valued key. */
function strArray(row: CatalogRow, ...keys: string[]): string[] {
  for (const key of keys) {
    const v = row[key];
    if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string' && x !== '');
  }
  return [];
}

/** Build a `"amount CUR"` price string (what `formatFeedPrice` expects) from
 *  the split amount/currency fields; '' when there's no positive amount. */
function priceString(row: CatalogRow, amountKeys: string[], currencyKeys: string[]): string {
  let amount: number | null = null;
  for (const k of amountKeys) {
    const raw = row[k];
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        amount = n;
        break;
      }
    }
  }
  if (amount === null || amount <= 0) return '';
  const currency = pick(row, ...currencyKeys);
  return currency ? `${String(amount)} ${currency}` : String(amount);
}

/** Normalise the connector's `productDetails` entries (camel or snake) into the
 *  snake-cased shape `groupProductDetails` expects. */
function toDetailEntries(value: unknown): SyncProductDetailEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      const o = entry as Record<string, unknown>;
      const name = str(o.attributeName ?? o.attribute_name);
      const attrValue = str(o.attributeValue ?? o.attribute_value);
      const section = str(o.sectionName ?? o.section_name);
      return {
        attribute_name: name,
        attribute_value: attrValue,
        section_name: section || null,
      };
    })
    .filter((e) => e.attribute_name && e.attribute_value);
}

/**
 * Map a loose Product Catalog list row to the onboarding `ProductDetail` shape
 * so the shared `ProductDetailPanel` can render it. Prefers the enriched
 * `optimizedImages`, then the raw `imageLink`, then `additionalImageLinks`;
 * builds price strings from the split amount/currency fields; and collapses
 * the `gtin`/`gtins` and `productType`/`productTypes` variants.
 */
export function toProductDetail(row: CatalogRow): ProductDetail {
  const optimized = strArray(row, 'optimizedImages', 'optimized_images');
  const mainImage = pick(row, 'imageLink', 'image_link', 'imageUrl', 'image_url');
  const primary = optimized.length > 0 ? optimized : mainImage ? [mainImage] : [];
  const additional = strArray(row, 'additionalImageLinks', 'additional_image_links');
  const images = [...new Set([...primary, ...additional])];

  const productType = strArray(row, 'productTypes', 'product_types')[0] ?? '';
  const gtins = strArray(row, 'gtins');
  const singleGtin = pick(row, 'gtin');

  return {
    id: pick(row, 'id', 'externalId', 'external_id', 'offerId', 'offer_id'),
    title: pick(row, 'title', 'name'),
    description: pick(row, 'description'),
    brand: pick(row, 'brand', 'vendor'),
    link: pick(row, 'link'),
    images,
    googleProductCategory: pick(
      row,
      'googleProductCategory',
      'google_product_category',
      'category',
    ),
    productType: productType || pick(row, 'productType', 'product_type'),
    itemGroupId: pick(row, 'itemGroupId', 'item_group_id'),
    offerId: pick(row, 'offerId', 'offer_id', 'externalId', 'external_id', 'id'),
    condition: pick(row, 'condition'),
    gender: pick(row, 'gender'),
    ageGroup: pick(row, 'ageGroup', 'age_group'),
    color: pick(row, 'color'),
    sizes: strArray(row, 'sizes'),
    material: pick(row, 'material'),
    pattern: pick(row, 'pattern'),
    mpn: pick(row, 'mpn'),
    gtins: gtins.length > 0 ? gtins : singleGtin ? [singleGtin] : [],
    availability: pick(row, 'availability'),
    price: priceString(row, ['priceAmount', 'price_amount'], ['priceCurrency', 'price_currency']),
    salePrice: priceString(
      row,
      ['salePriceAmount', 'sale_price_amount'],
      ['salePriceCurrency', 'sale_price_currency'],
    ),
    productHighlights: strArray(row, 'productHighlights', 'product_highlights'),
    productDetails: toDetailEntries(row.productDetails ?? row.product_details),
  };
}
